
import Konva from 'konva';
import { Component, Inject, Vue, Watch } from 'vue-property-decorator';
import VueKonva from 'vue-konva';
import { Marker } from './types';
import State from './state';
import { getActivities, getCell, getMarkerColor, getMarkerWithObjects, getFilteredMarkers } from './util';
import MarkerAddModal from './MarkerAddModal';

Vue.use(VueKonva);

type KonvaMouseEvent = {
  evt: MouseEvent | TouchEvent;
  target: Konva.Node;
};

@Component({
  components: {
    MarkerAddModal,
  },
})
export default class MarkerCanvas extends Vue {
  @Inject()
  readonly state!: State;

  imgEl: HTMLImageElement | null = null;
  naturalSize = { w: 0, h: 0 };

  viewport = { w: 800, h: 600 };

  fitScale = 1;
  zoom = 1;
  scale = 1;

  imagePos = { x: 0, y: 0 };

  hoveredMarkerId = '';

  private konvaAnim: Konva.Animation | null = null;

  // panning state (right mouse button drag)
  pan = {
    active: false,
    lastX: 0,
    lastY: 0,
  };

  get imageUrl(): string {
    return this.state.media?.publicUrl ?? '';
  };

  get drawnSize(): { w: number; h: number } {
    return { w: this.naturalSize.w * this.scale, h: this.naturalSize.h * this.scale };
  }

  get hoveredMarker(): Marker | undefined {
    return this.visibleMarkers.find((m) => m.id === this.hoveredMarkerId);
  }

  get imageConfig(): Konva.ImageConfig {
    return {
      x: this.imagePos.x,
      y: this.imagePos.y,
      image: this.imgEl ?? undefined,
      width: this.naturalSize.w * this.scale,
      height: this.naturalSize.h * this.scale,
      listening: false,
    };
  }

  get stageConfig(): Konva.StageConfig {
    return { width: this.viewport.w, height: this.viewport.h };
  }

  get tooltip(): { visible: boolean; x: number; y: number; text: string } {
    const ret = { visible: false, x: 0, y: 0, text: '' };
    const m = this.hoveredMarker;
    if (!m) {
      return ret;
    }

    const text = [
      m.label ?? null,
      getCell(m, this.state.cellStore)
          ? this.$t('maintenance.overview2d.marker.model.cellId').toString() + ': ' + getCell(m, this.state.cellStore)!.name
          : null,
      ...getActivities(m, this.state.activityStore)
          .map(a => this.$t('maintenance.overview2d.marker.activity').toString() + ': ' + a.title),
    ].filter(e => !!e).join('\n');
    if (!text) {
      return ret;
    }

    const p = this.normalizedToStage(m);
    return { visible: true, x: p.x + 14, y: p.y - 14, text: text };
  }

  get tooltipLabelConfig(): Konva.LabelConfig {
    return { x: this.tooltip.x, y: this.tooltip.y, listening: false };
  }

  get tooltipTagConfig(): Konva.TagConfig {
    return {
      fill: 'rgba(255,255,255,0.92)',
      stroke: 'rgba(0,0,0,0.25)',
      strokeWidth: 1,
      cornerRadius: 6,
      pointerDirection: 'down',
      pointerWidth: 10,
      pointerHeight: 6,
      lineJoin: 'round',
      shadowColor: 'rgba(0,0,0,0.25)',
      shadowBlur: 8,
      shadowOffset: { x: 0, y: 2 },
      shadowOpacity: 1,
    };
  }

  get tooltipTextConfig(): Konva.TextConfig {
    return { text: this.tooltip.text, fill: '#000', fontSize: 13, padding: 8 };
  }

  mounted(): void {
    this.state.activityStore.loadIfAbsent();
    this.updateViewportFromContainer();
    window.addEventListener('resize', this.onResize as any, { passive: true });

    //this.initPulseAnimation();

    // prevent native context menu on right-click over the Konva canvas container
    this.$nextTick(() => {
      const stage = this.getStage();
      const container: HTMLDivElement | undefined = stage?.container?.();
      container?.addEventListener('contextmenu', (e) => e.preventDefault());
    });
  }

  beforeDestroy(): void {
    window.removeEventListener('resize', this.onResize as any);
    this.konvaAnim?.stop();
  }

  clampPanToBounds(): void {
    const { w: drawnW, h: drawnH } = this.drawnSize;

    const minX = this.viewport.w - drawnW;
    const minY = this.viewport.h - drawnH;

    if (drawnW <= this.viewport.w) {
      this.imagePos.x = Math.floor((this.viewport.w - drawnW) / 2);
    } else {
      this.imagePos.x = Math.min(0, Math.max(minX, this.imagePos.x));
    }

    if (drawnH <= this.viewport.h) {
      this.imagePos.y = Math.floor((this.viewport.h - drawnH) / 2);
    } else {
      this.imagePos.y = Math.min(0, Math.max(minY, this.imagePos.y));
    }
  }

  confirmPendingAdd(marker: Marker): void {
    (this.$refs.addModal as MarkerAddModal).dismiss();

    this.state.markers.push(marker);
    this.state.selectedMarkerId = marker.id;
  }

  fitToContainer(): void {
    this.updateViewportFromContainer();
    this.zoom = 1;
    this.recomputeFit({ keepPan: false });
  }

  getStage(): Konva.Stage | undefined {
    const stageVm = this.$refs.stage as any;
    return stageVm?.getNode?.();
  }

  getStageContainer(): HTMLDivElement | undefined {
    return this.getStage()?.container?.();
  }

  isInsideImage(stageP: { x: number; y: number }) {
    const w = this.naturalSize.w * this.scale;
    const h = this.naturalSize.h * this.scale;
    return (
        stageP.x >= this.imagePos.x &&
        stageP.y >= this.imagePos.y &&
        stageP.x <= this.imagePos.x + w &&
        stageP.y <= this.imagePos.y + h
    );
  }

  labelConfig(m: Marker): Konva.TextConfig {
    const isSelected = m.id === this.state.selectedMarkerId;
    const p = this.normalizedToStage(m);

    return {
      x: p.x + 14,
      y: p.y - 7,
      text: m.label ?? '',
      fontSize: 14,
      fontStyle: isSelected ? 'bold' : 'normal',
      fill: '#000',
      listening: true,
    };
  }

  async loadImage(url: string): Promise<void> {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to load image'));
    });

    this.imgEl = img;
    this.naturalSize = { w: img.naturalWidth, h: img.naturalHeight };

    this.zoom = 1;
    this.recomputeFit({ keepPan: false });
  }

  initPulseAnimation(): void {
    // 1. Initialize the animation with just the frame handler loop
    this.konvaAnim = new Konva.Animation((frame) => {
      if (!frame) return;

      // Safe lookup inside the execution frame
      const layerVm = this.$refs.layer as any;
      const layerNode = layerVm?.getNode?.();
      if (!layerNode) return;

      const period = 1500; 
      const factor = (Math.sin((frame.time / period) * Math.PI * 2) + 1) / 2;
      const targetColor = this.getPulseColor(factor);

      // 2. Fetch all raw Konva Circle nodes on the layer
      const circles = layerNode.find('Circle');
    
      for (let i = 0; i < circles.length; i++) {
        const circle = circles[i];
        const attrs = circle.attrs;
      
        if (attrs.shadowColor === '#ff3b30' || attrs.shadowColor === 'red') {
          circle.fill(targetColor);
        }
      }

      // 3. Since we didn't pass the layer to the constructor, 
      // we manually batch draw the layer at the end of the frame processing
      layerNode.batchDraw();
    });

    // Start the animation loop safely
    this.konvaAnim.start();
  }

/**
 * Generates an internal fill color shifting between bright crimson and a deep burgundy
 */
getPulseColor(factor: number): string {
  // Bright crimson RGB
  const r1 = 255, g1 = 40, b1 = 40;
  // Deep muted burgundy RGB
  const r2 = 90, g2 = 10, b2 = 10;

  const r = Math.round(r1 + factor * (r2 - r1));
  const g = Math.round(g1 + factor * (g2 - g1));
  const b = Math.round(b1 + factor * (b2 - b1));

  return `rgb(${r}, ${g}, ${b})`;
}

markerConfig(m: Marker): Konva.CircleConfig {
  const isSelected = m.id === this.state.selectedMarkerId;
  const isHovered = m.id === this.hoveredMarkerId;
  const p = this.normalizedToStage(m);
  const baseColor = getMarkerColor(m, this.state.activityStore);

  let radius = 6;
  let shadowBlur = 10;
  let shadowOpacity = 0.8;
  let strokeWidth = 2;
  let strokeColor = '#ffffff';

  if (isSelected) {
    radius = 8;
    shadowBlur = 25;       
    shadowOpacity = 1.0;
    strokeWidth = 3;
  } else if (isHovered) {
    radius = 7;
    shadowBlur = 16;       
    shadowOpacity = 0.9;
  }

  const isIssueMarker = baseColor === 'red' || baseColor === '#dc3545';
  const stableGlowColor = isIssueMarker ? '#ff2a4b' : baseColor;

  return {
    x: p.x,
    y: p.y,
    radius: radius,
    fill: baseColor, // Initial static color fallback
    stroke: strokeColor,
    strokeWidth: strokeWidth,
    draggable: true,
    shadowColor: stableGlowColor, 
    shadowBlur: shadowBlur,       
    shadowOffset: { x: 0, y: 0 }, 
    shadowOpacity: shadowOpacity, 
    perfectDrawEnabled: true,
  };
}

  normalizedToStage(m: { nx: number; ny: number }): { x: number; y: number } {
    const { w: iw, h: ih } = this.naturalSize;
    const imgX = m.nx * iw;
    const imgY = m.ny * ih;

    return {
      x: this.imagePos.x + imgX * this.scale,
      y: this.imagePos.y + imgY * this.scale,
    };
  }

  onMarkerDragEnd(id: string, evt: any): void {
    const node: Konva.Circle = evt?.target;
    const pos = node?.position?.();
    if (!pos) {
      return;
    }

    const norm = this.stageToNormalized({ x: pos.x, y: pos.y });
    if (!norm) {
      return;
    }

    const marker = this.state.markers.find(m => m.id === id);
    if (marker) {
      marker.nx = norm.nx;
      marker.ny = norm.ny;
    }
  }

  onMarkerEnter(id: string): void {
    this.hoveredMarkerId = id;
    const container = this.getStageContainer();
    if (container) {
      container.style.cursor = 'pointer';
    }
  }

  onMarkerLeave(id: string): void {
    if (this.hoveredMarkerId === id) {
      this.hoveredMarkerId = '';
    }
    const container = this.getStageContainer();
    if (container) {
      container.style.cursor = this.pan.active ? 'grabbing' : 'default';
    }
  }

  onResize(): void {
    this.updateViewportFromContainer();
    this.recomputeFit({ keepPan: true });
  }

  onStageMouseDown(e: KonvaMouseEvent): void {
    const stage = this.getStage();
    if (!stage) {
      return;
    }

    // RIGHT BUTTON: start panning
    if (e.evt instanceof MouseEvent && e.evt.button === 2) {
      const { w: drawnW, h: drawnH } = this.drawnSize;
      const canPanX = drawnW > this.viewport.w;
      const canPanY = drawnH > this.viewport.h;
      if (!canPanX && !canPanY) {
        return;
      }

      e.evt.preventDefault();

      this.pan.active = true;
      this.pan.lastX = e.evt.clientX;
      this.pan.lastY = e.evt.clientY;

      const container = this.getStageContainer();
      if (container) {
        container.style.cursor = 'grabbing';
      }
      return;
    }

    // LEFT BUTTON: add marker (only when clicking empty stage)
    if (e.target !== stage) {
      return;
    }

    const p = this.toStagePointFromPointer();
    if (!p || !this.isInsideImage(p)) {
      return;
    }

    const norm = this.stageToNormalized(p);
    if (!norm) {
      return;
    }

    const marker: Marker = {
      id: crypto.randomUUID(),
      nx: norm.nx,
      ny: norm.ny,
      label: '',
      category: 'default',
      color: 'red',
    };

    (this.$refs.addModal as MarkerAddModal).open(marker);
  }

  onStageContextMenu(e: KonvaMouseEvent): void {
    (e.evt as any)?.preventDefault?.();
  }

  onStageMouseMove(e: KonvaMouseEvent): void {
    if (!this.pan.active) {
      return;
    }
    if (!(e.evt instanceof MouseEvent)) {
      return;
    }

    const dx = e.evt.clientX - this.pan.lastX;
    const dy = e.evt.clientY - this.pan.lastY;

    this.pan.lastX = e.evt.clientX;
    this.pan.lastY = e.evt.clientY;

    this.imagePos.x += dx;
    this.imagePos.y += dy;

    this.clampPanToBounds();
  }

  onStageMouseUp(e?: KonvaMouseEvent): void {
    if (!this.pan.active) {
      return;
    }

    this.pan.active = false;

    const container = this.getStageContainer();
    if (container) {
      container.style.cursor = 'default';
    }
  }

  recomputeFit(opts?: { keepPan?: boolean }): void {
    const keepPan = opts?.keepPan ?? false;

    const { w: iw, h: ih } = this.naturalSize;
    if (!iw || !ih) {
      return;
    }

    const pad = 10;
    const vw = Math.max(1, this.viewport.w - pad * 2);
    const vh = Math.max(1, this.viewport.h - pad * 2);

    this.fitScale = Math.min(vw / iw, vh / ih);
    this.scale = this.fitScale * this.zoom;

    const drawnW = iw * this.scale;
    const drawnH = ih * this.scale;

    if (!keepPan) {
      this.imagePos.x = Math.floor((this.viewport.w - drawnW) / 2);
      this.imagePos.y = Math.floor((this.viewport.h - drawnH) / 2);
    } else {
      this.clampPanToBounds();
    }
  }

  removeMarker(id: string): void {
    const index = this.state.markers.findIndex(m => m.id === id);
    if (index >= 0) {
      this.state.markers.splice(index, 1);
    }
    if (this.state.selectedMarkerId === id) {
      this.state.selectedMarkerId = null;
    }
    if (this.hoveredMarkerId === id) {
      this.hoveredMarkerId = '';
    }
  }

  stageToNormalized(p: { x: number; y: number }): { nx: number; ny: number } | null {
    const { w: iw, h: ih } = this.naturalSize;
    if (!iw || !ih) {
      return null;
    }

    const imgLocalX = (p.x - this.imagePos.x) / this.scale;
    const imgLocalY = (p.y - this.imagePos.y) / this.scale;

    const clampedX = Math.min(iw, Math.max(0, imgLocalX));
    const clampedY = Math.min(ih, Math.max(0, imgLocalY));

    return { nx: clampedX / iw, ny: clampedY / ih };
  }

  selectMarker(id: string): void {
    this.state.selectedMarkerId = id;
  }

  toStagePointFromPointer(): { x: number; y: number } | null {
    const stage = this.getStage();
    if (!stage) {
      return null;
    }

    const p = stage.getPointerPosition();
    if (!p) {
      return null;
    }

    return { x: p.x, y: p.y };
  }

  updateViewportFromContainer(): void {
    const wrap = this.$refs.wrap as HTMLDivElement | undefined;
    if (!wrap) {
      return;
    }
    const rect = wrap.getBoundingClientRect();
    this.viewport.w = Math.max(320, Math.floor(rect.width));
    this.viewport.h = Math.max(240, Math.floor(rect.height));
  }

  zoomIn(): void {
    this.zoom = Math.min(6, Number((this.zoom * 1.2).toFixed(4)));
    this.recomputeFit({ keepPan: true });
  }

  zoomOut(): void {
    this.zoom = Math.max(0.2, Number((this.zoom / 1.2).toFixed(4)));
    this.recomputeFit({ keepPan: true });
  }

  @Watch('imageUrl', { immediate: true })
  onImageUrlChanged() {
    if (!this.imageUrl) {
      return;
    }
    this.loadImage(this.imageUrl);
  }

  get visibleMarkers(): Marker[] {
    return getFilteredMarkers(this.state).map(item => item.marker);
  }

}
